// Frontend/src/lib/issueLabels.ts
//
// Non-component helpers for the issue pages. Kept out of IssueBits.tsx so
// that file exports components and nothing else — a mixed module breaks
// React Fast Refresh, which quietly turns every edit into a full reload.

import React from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import type { IssuePriority, IssueStatus } from "@/types/issue";

/**
 * Display names for the two enums that are still enums.
 *
 * Categories are not here: they are rows an HOA admin owns, so their names
 * come from the server and cannot be translated at build time. A building
 * that renames "Plumbing" to "Plomería y desagües" means exactly that.
 *
 * Spelled-out keys, never a template literal: check-i18n.py only matches
 * string-literal keys, so a computed one can go missing without CI noticing
 * and then renders as its own name in the UI.
 */
export function useIssueLabels() {
  const { t } = useLanguage();
  return {
    status: {
      PENDING: t("issues.status.PENDING"),
      IN_PROGRESS: t("issues.status.IN_PROGRESS"),
      RESOLVED: t("issues.status.RESOLVED"),
      CANCELLED: t("issues.status.CANCELLED"),
    } as Record<IssueStatus, string>,
    priority: {
      LOW: t("issues.priority.LOW"),
      MEDIUM: t("issues.priority.MEDIUM"),
      HIGH: t("issues.priority.HIGH"),
      URGENT: t("issues.priority.URGENT"),
    } as Record<IssuePriority, string>,
  };
}

export type IssueLabels = ReturnType<typeof useIssueLabels>;

export function formatBytes(bytes: number): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Flattens DRF's `{field: [message]}` into one readable line. */
export function readApiError(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (typeof data === "string" && data) return data;
  if (data && typeof data === "object") {
    const parts: string[] = [];
    for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
      const text = Array.isArray(value) ? value.join(" ") : String(value);
      parts.push(key === "detail" || key === "non_field_errors" ? text : `${key}: ${text}`);
    }
    if (parts.length) return parts.join(" · ");
  }
  return fallback;
}

/** How long ago, in words. Precise dates go in the metadata list. */
export function useRelativeAge() {
  const { t } = useLanguage();
  return React.useCallback(
    (iso: string) => {
      const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
      if (days <= 0) return t("issues.ageToday");
      if (days === 1) return t("issues.ageOneDay");
      return t("issues.ageDays", { days });
    },
    [t]
  );
}
